/**
 * Country reference data shared by the web country picker (@app/ui CountrySelect)
 * and the server-rendered supplier form (apps/api). Values are stored as ISO
 * 3166-1 alpha-2 codes; display names are DERIVED per locale via Intl.DisplayNames
 * (a built-in — no bundled name tables) and flags are DERIVED from the code via
 * regional-indicator emoji. So this module ships only the canonical code list.
 */

/**
 * ISO 3166-1 alpha-2 officially assigned codes. This is the persisted value
 * space; names/flags never persist — they are recomputed for the viewer's locale.
 */
export const COUNTRY_CODES = [
  "AD",
  "AE",
  "AF",
  "AG",
  "AI",
  "AL",
  "AM",
  "AO",
  "AQ",
  "AR",
  "AS",
  "AT",
  "AU",
  "AW",
  "AX",
  "AZ",
  "BA",
  "BB",
  "BD",
  "BE",
  "BF",
  "BG",
  "BH",
  "BI",
  "BJ",
  "BL",
  "BM",
  "BN",
  "BO",
  "BQ",
  "BR",
  "BS",
  "BT",
  "BV",
  "BW",
  "BY",
  "BZ",
  "CA",
  "CC",
  "CD",
  "CF",
  "CG",
  "CH",
  "CI",
  "CK",
  "CL",
  "CM",
  "CN",
  "CO",
  "CR",
  "CU",
  "CV",
  "CW",
  "CX",
  "CY",
  "CZ",
  "DE",
  "DJ",
  "DK",
  "DM",
  "DO",
  "DZ",
  "EC",
  "EE",
  "EG",
  "EH",
  "ER",
  "ES",
  "ET",
  "FI",
  "FJ",
  "FK",
  "FM",
  "FO",
  "FR",
  "GA",
  "GB",
  "GD",
  "GE",
  "GF",
  "GG",
  "GH",
  "GI",
  "GL",
  "GM",
  "GN",
  "GP",
  "GQ",
  "GR",
  "GS",
  "GT",
  "GU",
  "GW",
  "GY",
  "HK",
  "HM",
  "HN",
  "HR",
  "HT",
  "HU",
  "ID",
  "IE",
  "IL",
  "IM",
  "IN",
  "IO",
  "IQ",
  "IR",
  "IS",
  "IT",
  "JE",
  "JM",
  "JO",
  "JP",
  "KE",
  "KG",
  "KH",
  "KI",
  "KM",
  "KN",
  "KP",
  "KR",
  "KW",
  "KY",
  "KZ",
  "LA",
  "LB",
  "LC",
  "LI",
  "LK",
  "LR",
  "LS",
  "LT",
  "LU",
  "LV",
  "LY",
  "MA",
  "MC",
  "MD",
  "ME",
  "MF",
  "MG",
  "MH",
  "MK",
  "ML",
  "MM",
  "MN",
  "MO",
  "MP",
  "MQ",
  "MR",
  "MS",
  "MT",
  "MU",
  "MV",
  "MW",
  "MX",
  "MY",
  "MZ",
  "NA",
  "NC",
  "NE",
  "NF",
  "NG",
  "NI",
  "NL",
  "NO",
  "NP",
  "NR",
  "NU",
  "NZ",
  "OM",
  "PA",
  "PE",
  "PF",
  "PG",
  "PH",
  "PK",
  "PL",
  "PM",
  "PN",
  "PR",
  "PS",
  "PT",
  "PW",
  "PY",
  "QA",
  "RE",
  "RO",
  "RS",
  "RU",
  "RW",
  "SA",
  "SB",
  "SC",
  "SD",
  "SE",
  "SG",
  "SH",
  "SI",
  "SJ",
  "SK",
  "SL",
  "SM",
  "SN",
  "SO",
  "SR",
  "SS",
  "ST",
  "SV",
  "SX",
  "SY",
  "SZ",
  "TC",
  "TD",
  "TF",
  "TG",
  "TH",
  "TJ",
  "TK",
  "TL",
  "TM",
  "TN",
  "TO",
  "TR",
  "TT",
  "TV",
  "TW",
  "TZ",
  "UA",
  "UG",
  "UM",
  "US",
  "UY",
  "UZ",
  "VA",
  "VC",
  "VE",
  "VG",
  "VI",
  "VN",
  "VU",
  "WF",
  "WS",
  "YE",
  "YT",
  "ZA",
  "ZM",
  "ZW",
] as const;

export type CountryCode = (typeof COUNTRY_CODES)[number];

/**
 * App-wide default country. The product targets Turkish exporters (the map and
 * copy are Türkiye-centric), so a new country picker pre-selects TR. Kept as a
 * single constant so it can later be sourced from an org/user setting.
 */
export const DEFAULT_COUNTRY_CODE = "TR";

const CODE_SET: ReadonlySet<string> = new Set(COUNTRY_CODES);

/** True for a canonical ISO 3166-1 alpha-2 code (case-insensitive). */
export function isCountryCode(code: string): boolean {
  return CODE_SET.has(code.trim().toUpperCase());
}

/**
 * Regional-indicator flag emoji for an alpha-2 code (e.g. "TR" -> 🇹🇷). Returns
 * "" for anything that isn't two ASCII letters. Note: some platforms (notably
 * Windows) render the two letters instead of a flag glyph — callers always pair
 * this with the country name so the option stays unambiguous either way.
 */
export function countryFlagEmoji(code: string): string {
  const cc = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return "";
  const OFFSET = 0x1f1e6 - 0x41; // 'A' -> REGIONAL INDICATOR SYMBOL LETTER A
  return String.fromCodePoint(cc.charCodeAt(0) + OFFSET, cc.charCodeAt(1) + OFFSET);
}

// Intl.DisplayNames instances are relatively expensive to build; reuse one per locale.
const displayNamesCache = new Map<string, Intl.DisplayNames>();

function displayNamesFor(locale: string): Intl.DisplayNames {
  let dn = displayNamesCache.get(locale);
  if (!dn) {
    dn = new Intl.DisplayNames([locale], { type: "region" });
    displayNamesCache.set(locale, dn);
  }
  return dn;
}

/**
 * Localized country name for a code (e.g. countryDisplayName("DE", "tr") ->
 * "Almanya"). Falls back to the upper-cased code for unknown values so legacy
 * free-text entries still render as themselves.
 */
export function countryDisplayName(code: string, locale = "en"): string {
  const cc = code.trim().toUpperCase();
  try {
    return displayNamesFor(locale).of(cc) ?? cc;
  } catch {
    return cc;
  }
}

export interface CountryOption {
  code: string;
  /** Localized display name. */
  name: string;
  /** Flag emoji (may be "" on platforms without flag glyphs). */
  flag: string;
}

/** All countries as { code, name, flag }, sorted by localized name for the given locale. */
export function countryOptions(locale = "en"): CountryOption[] {
  return COUNTRY_CODES.map((code) => ({
    code,
    name: countryDisplayName(code, locale),
    flag: countryFlagEmoji(code),
  })).sort((a, b) => a.name.localeCompare(b.name, locale));
}
